import { IGenerateClass2SmartWorksheetReq } from '@models/interfaces/worksheet.interface';
import StudentService, {
  optionalParamProps,
} from '@services/GeniebookAPI/StudentServices.services';
import { useMutation, useQueries, useQuery } from '@tanstack/react-query';
import { SLOW_DATA_STALE_TIME_IN_SECONDS } from '@utils/constants/global.constants';
import {
  ICheckNewAIComment,
  IConsultWithECFormData,
  IGetMasteryLevelProgressDataFormData,
  IGetPresetWorksheetsFormData,
  IGetStatisticFormData,
  IGetStudentAbGroupInfoFormData,
  IGetStudentMasteryWatchTimeFormData,
  IGetStudentScoreProgressDataFormData,
  IGetStudentTopicsFormData,
  IGetStudentTopicsMasteryLevelFormData,
} from '@models/interfaces/student.interface';
import AICommentServices from '@services/GeniebookAPI/AICommentServices.services';

export const useSubjectMenuList = () => {
  return useQuery(['subjectMenuList'], StudentService.subject_menu_list, {
    staleTime: SLOW_DATA_STALE_TIME_IN_SECONDS * 1000,
  });
};

export const useBubbleTransactionData = () => {
  return useQuery(
    ['bubbleTransactionData'],
    StudentService.getBubbleTransactionData,
  );
};

export const useConsultWithEC = () => {
  return useMutation((formData: IConsultWithECFormData) =>
    StudentService.consultWithEC(formData),
  );
};

// create useQuery hooks to get data from API checkEarnedBadges
export const useCheckEarnedBadges = () => {
  return useQuery(['checkEarnedBadges'], StudentService.checkEarnedBadges, {
    cacheTime: 0,
  });
};

// create useQuery to get badges data
export const useGetBadges = () => {
  return useQuery(['getBadges'], StudentService.getBadges, {
    cacheTime: 0,
  });
};

export const useStudentTopics = (formData: IGetStudentTopicsFormData) => {
  return useQuery(['studentTopics', formData], () =>
    StudentService.getStudentTopics(formData),
  );
};
export const useStudentStatistics = (formData: IGetStatisticFormData) => {
  return useQuery({
    queryKey: ['studentStatistics', formData],
    queryFn: () => StudentService.getStatistic(formData),
    enabled: !!formData.student_id && !!formData.subject_id,
  });
};
export const useMasteryStudentTopics = (
  formData: IGetStudentTopicsMasteryLevelFormData,
  haveMastery: boolean,
) => {
  return useQuery({
    queryKey: ['masteryStudentTopics', formData],
    queryFn: () => StudentService.getStudentTopicsMasteryLevel(formData),
    enabled: haveMastery,
  });
};

export const useProgressScores = (
  formData: IGetStudentScoreProgressDataFormData[],
) => {
  return useQueries({
    queries: formData.map(item => {
      return {
        queryKey: ['progressScoresData', item],
        queryFn: async () => {
          const rawData =
            await StudentService.getStudentScoreProgressData(item);
          return rawData.data?.data;
        },
        enabled: !!item?.subject_id,
      };
    }),
  });
};

export const useMasteryLevels = (
  formData: IGetMasteryLevelProgressDataFormData,
  isTestAccount: boolean,
) => {
  return useQuery({
    queryKey: ['masteryLevels', formData],
    queryFn: async () => {
      const rawData =
        await StudentService.getMasteryLevelProgressData(formData);
      return rawData.data?.data ?? null;
    },
    enabled: !!formData?.subject_id && !isTestAccount,
  });
};

export const usePresetWorksheets = (formData: IGetPresetWorksheetsFormData) => {
  return useQuery({
    queryKey: ['studentPreset', formData],
    queryFn: () => StudentService.getPresetWorksheets(formData),
    staleTime: SLOW_DATA_STALE_TIME_IN_SECONDS * 1000,
    enabled: !!formData?.subject_id && !!formData?.student_id,
  });
};

export function useAbGroupStatus(formData: IGetStudentAbGroupInfoFormData) {
  return useQuery({
    queryKey: ['AbGroupData', formData],
    queryFn: async () => {
      const rawData = await StudentService.getStudentAbGroupInfo(formData);
      return rawData.data?.data;
    },
    enabled: !!formData?.subject_id,
  });
}

export const useGenerateClass2SmartWorksheet = () => {
  return useMutation((formData: IGenerateClass2SmartWorksheetReq) =>
    StudentService.generateClass2SmartWorksheet(formData),
  );
};

export const useStudentExamResult = (
  count: number,
  page: number,
  param?: optionalParamProps,
) => {
  return useQuery({
    queryKey: ['studentExamResultList', count, page, param],
    queryFn: () => StudentService.getStudentExamResultList(count, page, param),
    staleTime: 0,
  });
};

export const useStudentExamResultFilterOptions = (
  type: 'level' | 'exam' | 'subject',
) => {
  return useQuery({
    queryKey: ['examResultFilterOptions', type],
    queryFn: () => StudentService.getStudentExamResultFilterOptions(type),
  });
};

export const useStudentExamResultDetail = (exam_score_id: string | null) => {
  const queryKey = ['examResultDetail', exam_score_id];

  const queryFn = () => {
    if (!exam_score_id || exam_score_id === '') {
      return Promise.resolve(null);
    }

    return StudentService.getStudentExamResultDetail(exam_score_id);
  };

  return useQuery({
    queryKey,
    queryFn,
  });
};

export const useDeleteExamResult = () => {
  return useMutation((formData: FormData) =>
    StudentService.deleteStudentExamResult(formData),
  );
};

export const useStudentWatchTime = (
  formData: IGetStudentMasteryWatchTimeFormData,
) => {
  return useQuery({
    queryKey: ['studentWatchTime', formData],
    queryFn: () => StudentService.getStudentMasteryWatchTime(formData),
  });
};

export const useCheckNewAIComment = (formData: ICheckNewAIComment) => {
  return useQuery({
    queryKey: ['checkNewAIComment', formData],
    queryFn: () => AICommentServices.checkNewAIComment(formData),
  });
};
